import { collection, getDocs, query } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Get clients from IndexedDB first, fallback to Firestore
 * @returns {Promise<Array>} Array of client documents
 */
const getAllClientsFromCache = async () => {
  try {
    // Try IndexedDB first
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('clients')) {
          // No clients cache, fallback to Firestore
          resolve(null);
          return;
        }
        
        const transaction = db.transaction(['clients'], 'readonly');
        const store = transaction.objectStore('clients');
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          const clients = getAllRequest.result;
          if (clients && clients.length > 0) {
            resolve(clients);
          } else {
            resolve(null); // Empty cache, fallback to Firestore
          }
        };
        
        getAllRequest.onerror = () => resolve(null);
      };
      
      request.onerror = () => resolve(null);
    });
  } catch (error) {
    console.warn('Error accessing IndexedDB for client validation:', error);
    return null;
  }
};

/**
 * Sync clients to IndexedDB for validation caching
 * @param {Array} clients - Client documents to cache
 */
const syncClientsToValidationCache = async (clients) => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    request.onsuccess = () => {
      const db = request.result;
      
      if (!db.objectStoreNames.contains('clients')) {
        return; // Can't cache without object store
      }
      
      const transaction = db.transaction(['clients'], 'readwrite');
      const store = transaction.objectStore('clients');
      
      // Clear and repopulate cache
      store.clear();
      clients.forEach(client => {
        store.put(client);
      });
    };
  } catch (error) {
    console.warn('Error syncing clients to validation cache:', error);
  }
};

/**
 * Check for duplicate phone numbers and emails across all clients
 * @param {Array} contacts - Array of contacts to validate
 * @param {string} excludeClientId - Client ID to exclude from duplicate check (for editing)
 * @returns {Object} Validation results with duplicates found
 */
export const checkClientContactDuplicates = async (contacts, excludeClientId = null) => {
  try {
    // Try to get clients from IndexedDB cache first
    let clientsData = await getAllClientsFromCache();
    
    if (!clientsData) {
      // Fallback to Firestore if no cache available
      console.log('No client cache available, fetching from Firestore for validation');
      const q = query(collection(db, 'clients'));
      const querySnapshot = await getDocs(q);
      
      // Convert to array format and cache for next time
      clientsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Cache for future validation calls
      await syncClientsToValidationCache(clientsData);
    }
    
    const duplicates = {
      phones: [],
      emails: []
    };
    
    // Build arrays of phone numbers and emails to check
    const phonesToCheck = contacts
      .map(contact => contact.telefono?.replace(/\s/g, ''))
      .filter(phone => phone && phone.length > 0);
    
    const emailsToCheck = contacts
      .map(contact => contact.email?.toLowerCase().trim())
      .filter(email => email && email.length > 0);
    
    // Check each existing client
    clientsData.forEach(client => {
      const clientData = client;
      const clientId = client.id;
      
      // Skip the current client being edited
      if (excludeClientId && clientId === excludeClientId) {
        return;
      }
      
      const clientName = clientData.nombreEmpresa || 
        (clientData.contactos?.find(c => c.esPrincipal)?.nombre) || 
        'Cliente sin nombre';
      
      // Check each contact in the existing client
      (clientData.contactos || []).forEach(existingContact => {
        // Check for phone duplicates
        const existingPhone = existingContact.telefono?.replace(/\s/g, '');
        if (existingPhone && phonesToCheck.includes(existingPhone)) {
          duplicates.phones.push({
            phone: existingPhone,
            clientName: clientName,
            contactName: existingContact.nombre || 'Contacto sin nombre',
            clientId: clientId
          });
        }
        
        // Check for email duplicates
        const existingEmail = existingContact.email?.toLowerCase().trim();
        if (existingEmail && emailsToCheck.includes(existingEmail)) {
          duplicates.emails.push({
            email: existingEmail,
            clientName: clientName,
            contactName: existingContact.nombre || 'Contacto sin nombre',
            clientId: clientId
          });
        }
      });
    });
    
    return {
      success: true,
      duplicates,
      hasDuplicates: duplicates.phones.length > 0 || duplicates.emails.length > 0
    };
    
  } catch (error) {
    console.error('Error checking client contact duplicates:', error);
    return {
      success: false,
      error: 'Error al verificar duplicados',
      duplicates: { phones: [], emails: [] },
      hasDuplicates: false
    };
  }
};

/**
 * Format duplicate error messages for display
 * @param {Object} duplicates - Duplicates object from checkClientContactDuplicates
 * @returns {Array} Array of formatted error messages
 */
export const formatDuplicateMessages = (duplicates) => {
  const messages = [];
  
  // Format phone duplicate messages
  duplicates.phones.forEach(duplicate => {
    messages.push(
      `El teléfono ${duplicate.phone} ya está registrado para el cliente "${duplicate.clientName}" (contacto: ${duplicate.contactName})`
    );
  });
  
  // Format email duplicate messages
  duplicates.emails.forEach(duplicate => {
    messages.push(
      `El email ${duplicate.email} ya está registrado para el cliente "${duplicate.clientName}" (contacto: ${duplicate.contactName})`
    );
  });
  
  return messages;
};

/**
 * Validate contacts for internal duplicates within the same client
 * @param {Array} contacts - Array of contacts to validate
 * @returns {Object} Validation results for internal duplicates
 */
export const validateInternalDuplicates = (contacts) => {
  const errors = [];
  const seenPhones = new Set();
  const seenEmails = new Set();
  
  contacts.forEach((contact, index) => {
    // Check phone duplicates
    const phone = contact.telefono?.replace(/\s/g, '');
    if (phone && phone.length > 0) {
      if (seenPhones.has(phone)) {
        errors.push({
          type: 'phone',
          contactIndex: index,
          message: `El teléfono ${phone} está duplicado en este cliente`
        });
      } else {
        seenPhones.add(phone);
      }
    }
    
    // Check email duplicates
    const email = contact.email?.toLowerCase().trim();
    if (email && email.length > 0) {
      if (seenEmails.has(email)) {
        errors.push({
          type: 'email',
          contactIndex: index,
          message: `El email ${email} está duplicado en este cliente`
        });
      } else {
        seenEmails.add(email);
      }
    }
  });
  
  return {
    hasInternalDuplicates: errors.length > 0,
    errors
  };
};